/**
 * FinancialsTab Component
 *
 * Renders the Financials tab content for CompanyDetails, including:
 * - Tab switcher (Consolidated/Individual)
 * - Financial groups with level filtering and collapse/expand
 * - Financial data tables with synchronized row heights
 *
 * SELF-CONTAINED MODE: When `financials` prop is provided (instead of tabs/groups),
 * this component manages all internal state including tab selection, collapse state,
 * and level filtering.
 *
 * CONTROLLED MODE: When `tabs` and `groups` props are provided, parent manages state.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import ProfilerCurrencyFormatSwitch from '../../../controls/ProfilerCurrencyFormatSwitch';
import {
  METRIC_GROUP,
  PROFILER_FINANCIAL_STATEMENTS
} from '../../../../utils/constants/profilerFinancials';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';

/**
 * Tab the self-contained mode opens on. Which scope to prefer is the caller's call
 * (`initialScope`); this only makes sure the tab it opens on has data.
 */
const resolveInitialTab = ({ initialScope, isConsolidatedDataEmpty, isIndividualDataEmpty }) => {
  if (isConsolidatedDataEmpty && !isIndividualDataEmpty) return 'individual';
  if (isIndividualDataEmpty) return 'consolidated';
  return initialScope === 'individual' ? 'individual' : 'consolidated';
};

/**
 * @param {Object} props
 * CONTROLLED MODE PROPS:
 * @param {Array} props.tabs - Tab definitions (consolidated/individual)
 * @param {Array} props.groups - Financial groups to display
 * @param {Array} props.financialYears - Years to display in columns
 * @param {Object} props.collapsedGroups - Map of group.id -> boolean
 * @param {Object} props.toggledFilters - Map of group.id -> boolean (filter popup open)
 * @param {Object} props.selectedLevels - Map of group.id -> level number
 * @param {Function} props.onToggleFilter - Callback(groupId)
 * @param {Function} props.onSetLevel - Callback(groupId, level)
 * @param {Function} props.onToggleGroup - Callback(groupId)
 *
 * SELF-CONTAINED MODE PROPS:
 * @param {Object} props.financials - Raw financials data from API
 * @param {boolean} props.isConsolidatedDataEmpty - Whether consolidated data is empty
 * @param {boolean} props.isIndividualDataEmpty - Whether individual data is empty
 * @param {string} [props.initialScope] - 'consolidated' | 'individual': scope to open on
 *   when both have data
 *
 * COMMON PROPS:
 * @param {string} props.layout - 'desktop' | 'mobile' | 'tablet'
 * @param {string} props.formatUnit - 'euroMillions' | 'euroThousands'
 * @param {Function} props.formatCurrency - Function to format currency values
 */
const FinancialsTab = ({
  // Controlled mode props
  tabs: tabsProp,
  groups: groupsProp,
  financialYears: financialYearsProp,
  collapsedGroups: collapsedGroupsProp,
  toggledFilters: toggledFiltersProp,
  selectedLevels: selectedLevelsProp,
  onToggleFilter: onToggleFilterProp,
  onSetLevel: onSetLevelProp,
  onToggleGroup: onToggleGroupProp,
  // Self-contained mode props
  financials,
  isConsolidatedDataEmpty = false,
  isIndividualDataEmpty = false,
  initialScope,
  // Common props
  layout = 'desktop',
  formatUnit = 'euroMillions',
  formatCurrency = (v) => v
}) => {
  // Detect mode: self-contained if financials prop provided
  const isSelfContained = financials !== undefined;

  // ============================================
  // Self-contained mode state
  // ============================================
  const resolvedInitialTab = resolveInitialTab({
    initialScope,
    isConsolidatedDataEmpty,
    isIndividualDataEmpty
  });
  const [activeTab, setActiveTab] = useState(resolvedInitialTab);
  const [internalCollapsedGroups, setInternalCollapsedGroups] = useState({});
  const [internalToggledFilters, setInternalToggledFilters] = useState({});
  const [internalSelectedLevels, setInternalSelectedLevels] = useState({});

  // Refs for click-outside detection on filter popups
  const filterPopupRefs = useRef({});

  // Compute effective level for a group (derived state pattern - avoids re-render on init)
  // Returns user-selected level if set, otherwise default (last level = show all)
  // Note: null means "cleared" (show all levels), undefined means "use default"
  const getEffectiveLevel = useCallback(
    (groupId, group) => {
      const selectedLevel = internalSelectedLevels[groupId];
      // null = cleared (show all), undefined = not set yet
      if (selectedLevel === null) {
        // Cleared: show all levels (use last level)
        return group?.levels?.[group.levels.length - 1] ?? 1;
      }
      if (selectedLevel !== undefined) {
        return selectedLevel;
      }
      // Default: prefer level 2 if available, otherwise use last level
      return group?.levels?.includes(2) ? 2 : (group?.levels?.[group.levels.length - 1] ?? 1);
    },
    [internalSelectedLevels]
  );

  // Build tabs for self-contained mode
  const selfContainedTabs = useMemo(() => {
    if (!isSelfContained) return [];
    return [
      {
        id: 'financials-consolidated-tab',
        name: 'Consolidated',
        value: 'consolidated',
        isDisabled: isConsolidatedDataEmpty,
        isSelected: activeTab === 'consolidated',
        onClick: () => !isConsolidatedDataEmpty && setActiveTab('consolidated')
      },
      {
        id: 'financials-individual-tab',
        name: 'Individual',
        value: 'individual',
        isDisabled: isIndividualDataEmpty,
        isSelected: activeTab === 'individual',
        onClick: () => !isIndividualDataEmpty && setActiveTab('individual')
      }
    ];
  }, [isSelfContained, activeTab, isConsolidatedDataEmpty, isIndividualDataEmpty]);

  // Compute groups and years for self-contained mode
  const selfContainedData = useMemo(() => {
    if (!isSelfContained || !financials?.details) return { groups: [], years: [] };

    const dataSource =
      activeTab === 'consolidated'
        ? financials.details.consolidated
        : financials.details.individual;

    const rawGroups = dataSource?.groups || [];
    const years = dataSource?.detailsYears || [];

    // Filter items by effective level (derived state - no initialization needed)
    const filteredGroups = rawGroups.map((group) => {
      const effectiveLevel = getEffectiveLevel(group.id, group);
      return {
        ...group,
        items: group.items?.filter((item) => item.level <= effectiveLevel) || []
      };
    });

    return { groups: filteredGroups, years };
  }, [isSelfContained, financials, activeTab, getEffectiveLevel]);

  // Self-contained handlers
  const handleToggleFilter = useCallback(
    (groupId) => {
      if (onToggleFilterProp) {
        onToggleFilterProp(groupId);
      } else {
        setInternalToggledFilters((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
      }
    },
    [onToggleFilterProp]
  );

  const handleSetLevel = useCallback(
    (groupId, level) => {
      if (onSetLevelProp) {
        onSetLevelProp(groupId, level);
      } else {
        setInternalSelectedLevels((prev) => ({ ...prev, [groupId]: level }));
        setInternalToggledFilters((prev) => ({ ...prev, [groupId]: false }));
      }
    },
    [onSetLevelProp]
  );

  const handleToggleGroup = useCallback(
    (groupId) => {
      if (onToggleGroupProp) {
        onToggleGroupProp(groupId);
      } else {
        setInternalCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
      }
    },
    [onToggleGroupProp]
  );

  // Use controlled or self-contained values
  const tabs = isSelfContained ? selfContainedTabs : tabsProp || [];
  const groups = isSelfContained ? selfContainedData.groups : groupsProp || [];
  const financialYears = isSelfContained ? selfContainedData.years : financialYearsProp || [];
  const collapsedGroups = isSelfContained ? internalCollapsedGroups : collapsedGroupsProp || {};
  const toggledFilters = isSelfContained ? internalToggledFilters : toggledFiltersProp || {};
  const selectedLevels = isSelfContained ? internalSelectedLevels : selectedLevelsProp || {};
  const onToggleFilter = handleToggleFilter;
  const onSetLevel = handleSetLevel;
  const onToggleGroup = handleToggleGroup;

  // Labels and values are two separate stacks of rows, so their heights are synced here.
  // Each label row (and each table's year header) is a height source: it sizes to its own
  // content and is never given a height. A ResizeObserver copies a source's height onto its
  // target row in the other column whenever it changes (web font swap, viewport or
  // orientation change, tab or level switch), so text that wraps later can never spill over
  // its neighbours. The copy is written straight to the DOM, so a resize re-renders nothing.
  // Heights stay unrounded: rows can be fractional, and rounding each one would drift the
  // columns apart row by row.
  const heightTargets = useRef(new Map());
  const heightObserver = useRef(null);

  const registerHeightTarget = useCallback((el) => {
    if (!el) return undefined;
    const targets = heightTargets.current;
    targets.set(el.id, el);
    return () => targets.delete(el.id);
  }, []);

  const observeHeightSource = useCallback((el) => {
    // No ResizeObserver (jsdom): rows keep their natural heights, which only costs alignment
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    heightObserver.current ??= new ResizeObserver((entries) => {
      entries.forEach(({ target: source, borderBoxSize }) => {
        // borderBoxSize is missing before Safari 15.4; offsetHeight would round the fallback
        const height = borderBoxSize?.[0]?.blockSize ?? source.getBoundingClientRect().height;
        const target = heightTargets.current.get(source.dataset.heightTarget);
        if (target && height) target.style.height = `${height}px`;
      });
    });
    const observer = heightObserver.current;
    observer.observe(el);
    return () => observer.unobserve(el);
  }, []);

  useEffect(
    () => () => {
      heightObserver.current?.disconnect();
      heightObserver.current = null;
    },
    []
  );

  // Keep the tab in line with the data if it changes while mounted (self-contained mode).
  // A new company gets a fresh instance (the host keys this component by company), and a
  // tab the user picked stays until the data changes.
  useEffect(() => {
    if (!isSelfContained) return;
    setActiveTab(resolvedInitialTab);
  }, [isSelfContained, resolvedInitialTab]);

  // Click-outside handler for filter dropdowns (self-contained mode)
  useEffect(() => {
    if (!isSelfContained) return;

    // Find any open filter
    const openFilterId = Object.entries(internalToggledFilters).find(([, isOpen]) => isOpen)?.[0];
    if (!openFilterId) return;

    const handleClickOutside = (event) => {
      const popupEl = filterPopupRefs.current[openFilterId];
      if (popupEl && !popupEl.contains(event.target)) {
        setInternalToggledFilters((prev) => ({ ...prev, [openFilterId]: false }));
      }
    };

    // Add listener with slight delay to avoid immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelfContained, internalToggledFilters]);

  // Auto-scroll financials tables to show most recent data (rightmost columns)
  // This effect triggers when tab changes or format unit changes in self-contained mode
  useEffect(() => {
    if (!isSelfContained) return; // Only handle in self-contained mode

    const scrollTables = () => {
      const desktopFinancialsDOM = [
        ...document.querySelectorAll('.financials-table > .right-content')
      ];
      if (!desktopFinancialsDOM.length) return;

      if (desktopFinancialsDOM.length === 1 && desktopFinancialsDOM[0].scrollWidth) {
        desktopFinancialsDOM[0].scrollTo({
          left: desktopFinancialsDOM[0].scrollWidth,
          behavior: 'smooth'
        });
      } else if (desktopFinancialsDOM.length) {
        // Scroll all tables except first instantly
        const tablesToInstaScroll = desktopFinancialsDOM.slice(1);
        tablesToInstaScroll.reverse();
        tablesToInstaScroll.forEach((rightContent) => {
          if (rightContent.scrollWidth) {
            rightContent.scrollTo({ left: rightContent.scrollWidth });
          }
        });

        // Scroll first table with animation
        if (desktopFinancialsDOM[0].scrollWidth) {
          desktopFinancialsDOM[0].scrollTo({
            left: desktopFinancialsDOM[0].scrollWidth,
            behavior: 'smooth'
          });
        }
      }
    };

    // Delay to allow DOM to render after tab switch
    const timeoutId = setTimeout(scrollTables, 500);
    return () => clearTimeout(timeoutId);
  }, [isSelfContained, activeTab, formatUnit]);

  // Calculate column width based on layout and format unit
  const getColumnWidth = () => {
    if (layout === 'desktop') {
      return formatUnit === 'euroMillions' ? '157px' : '170px';
    }
    return formatUnit === 'euroMillions' ? '95px' : '140px';
  };

  const columnWidth = getColumnWidth();

  // Calculate indentation padding based on layout
  const getIndentPadding = (level) => {
    if (level <= 1) return {};
    const multiplier = layout === 'mobile' ? 4 : 16;
    return { paddingLeft: `${(level - 1) * multiplier}px` };
  };

  return (
    <div className="financials-tab-content">
      {/* Tab Switcher */}
      <div className="financials-tabs-wrap">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`financials-tab noselect ${tab.isDisabled ? 'disabled' : ''} ${
              tab.isSelected ? 'selected' : ''
            }`}
            role="button"
            tabIndex={tab.isDisabled ? -1 : 0}
            onClick={tab.onClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                tab.onClick();
              }
            }}
          >
            {tab.name}
          </div>
        ))}
      </div>

      {/* Financial Groups */}
      {groups.map((group, groupIdx) => {
        const hasLevels = !!group.levels?.length;
        const isCollapsed = collapsedGroups[group.id];
        const isFilterToggled = toggledFilters[group.id];
        const lastLevel = group.levels?.[group.levels.length - 1];
        // For self-contained mode, compute the effective level
        // null = cleared (show all), undefined = default (level 2 if available)
        const storedLevel = selectedLevels[group.id];
        const defaultLevel = group.levels?.includes(2) ? 2 : lastLevel;
        let effectiveLevel;
        if (storedLevel === null) {
          effectiveLevel = lastLevel; // Cleared: show all
        } else if (storedLevel !== undefined) {
          effectiveLevel = storedLevel; // User-selected
        } else {
          effectiveLevel = defaultLevel; // Default: prefer level 2
        }
        // Show filter indicator when:
        // - Controlled mode: user has explicitly selected a level
        // - Self-contained mode: level differs from "show all" AND not cleared
        const hasSelectedLevel = isSelfContained
          ? storedLevel !== null && effectiveLevel !== lastLevel
          : !!selectedLevels[group.id];
        const selectedLevel = effectiveLevel;

        return (
          <div key={group.id + groupIdx} className="group-wrap">
            {/* Group Header */}
            <div className="group-head">
              {/* Level Filter (only for groups with levels) */}
              {hasLevels && (
                <div
                  className="top-row"
                  ref={(el) => {
                    filterPopupRefs.current[group.id] = el;
                  }}
                >
                  <IconButton
                    hoverType={ICON_BUTTON_TYPES.BLUE}
                    icon={ICON_BUTTON_ICONS.FILTER}
                    innerText={`Filter by level${hasSelectedLevel ? ' (' + selectedLevel + ')' : ''}`}
                    withBorder={hasSelectedLevel}
                    onClick={() => {
                      if (toggledFilters[group.id]) {
                        return;
                      }
                      onToggleFilter(group.id);
                    }}
                    filled={isFilterToggled}
                  />
                  {isFilterToggled && (
                    <div className="lvl-filter-popup">
                      <div className="lvl-buttons-wrap">
                        {group.levels.map((lvl) => {
                          const isSelected = hasSelectedLevel && selectedLevel === lvl;
                          return (
                            <IconButton
                              key={lvl}
                              type={isSelected ? ICON_BUTTON_TYPES.BLUE : ICON_BUTTON_TYPES.DEFAULT}
                              filled={isSelected}
                              hoverType={ICON_BUTTON_TYPES.BLUE}
                              innerText={`Up to level ${lvl}`}
                              withBorder={false}
                              onClick={() => onSetLevel(group.id, lvl)}
                            />
                          );
                        })}
                      </div>
                      {hasSelectedLevel && (
                        <div className="filter-action-buttons-wrap">
                          <IconButton
                            disabled={!hasSelectedLevel}
                            type={ICON_BUTTON_TYPES.RED}
                            innerText="Clear filter"
                            onClick={() => onSetLevel(group.id, null)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Title and Collapse Toggle */}
              <div className="bottom-row">
                <div className={`group-title ${dataSourceClass(FIELD_SOURCE.financials)}`}>
                  {group.title}
                </div>
                <IconButton
                  icon={isCollapsed ? ICON_BUTTON_ICONS.CHEVRON_DOWN : ICON_BUTTON_ICONS.CHEVRON_UP}
                  innerText={isCollapsed ? 'Expand  ' : 'Collapse'}
                  withBorder={false}
                  iconOnRight
                  onClick={() => onToggleGroup(group.id)}
                />
              </div>

              {/* Mobile Scroll Indicator */}
              {!isCollapsed && layout === 'mobile' && (
                <div className="mobile-scrollable-content-row">
                  <IconButton
                    wrapClassName="company-details-financials-mobile-row-info-scroll-btn"
                    type={ICON_BUTTON_TYPES.BLUE}
                    icon={ICON_BUTTON_ICONS.ARROW_LEFT_RIGHT}
                    withBorder={false}
                    tooltipClassName="company-details-financials-mobile-row-info-scroll-tooltip"
                    tooltipContent={
                      <div>
                        <div className="info-title">Scrollable content</div>
                        <div>
                          <img
                            src="/images/company-details-financials-scrollable-info-mobile.gif"
                            alt=""
                          />
                        </div>
                      </div>
                    }
                  />
                </div>
              )}
            </div>

            {/* Empty State */}
            {!isCollapsed && !group.items?.length && (
              <div className="no-financials-items">There is no data found.</div>
            )}

            {/* Financial Table */}
            {!isCollapsed && !!group.items?.length && (
              <div className="financials-table">
                {/* Left Content - Labels */}
                <div className="left-content">
                  {/* Header Row */}
                  <div className="row" id={`fhead-${group.id}`} ref={registerHeightTarget}>
                    <div className="row-cell property">
                      <div className="label">
                        <ProfilerCurrencyFormatSwitch />
                      </div>
                    </div>
                  </div>

                  {/* Item Rows */}
                  {group.items.map((item, idx) => {
                    const itemKey = item.name + idx;
                    const prevItem = group.items[idx - 1];
                    const shouldRemoveTopBorder = item.level === 1 && prevItem?.level === 1;

                    return (
                      <div
                        key={itemKey}
                        className={`row lvl${item.level}`}
                        id={`fprop-${group.id}-${item.name}-${idx}`}
                        data-height-target={`frow-${group.id}-${item.name}-${idx}`}
                        style={shouldRemoveTopBorder ? { borderTop: 'none' } : undefined}
                        ref={observeHeightSource}
                      >
                        <div className="row-cell property">
                          <div className="label" style={getIndentPadding(item.level)}>
                            {item.name}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right Content - Values */}
                <div className="right-content" id={`right-financial-content-${group.id}`}>
                  {/* Header Row with Years */}
                  <div
                    className="row"
                    data-height-target={`fhead-${group.id}`}
                    ref={observeHeightSource}
                  >
                    {financialYears.map((year, yearIdx) => {
                      // Always render the financial-framework line so the "Actual"
                      // figures below stay on their correct row even for years without
                      // an accounting framework (e.g. self-disclosed revenue, which
                      // reports legal_framework as 'unknown'). Fall back to a placeholder
                      // dash to preserve the row height. TD-2546.
                      const framework = group.legal_framework?.[year];
                      const frameworkLabel =
                        framework && framework !== 'unknown' ? framework : PUNCTUATION.HYPHEN;

                      return (
                        <div
                          className="row-cell"
                          key={year + '' + yearIdx}
                          style={{ width: columnWidth, alignSelf: 'flex-start' }}
                        >
                          <div className="label">
                            {year}
                            <div>{frameworkLabel}</div>
                            {!!group.reliability?.[year] && <div>{group.reliability[year]}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Value Rows */}
                  {group.items.map((item, idx) => {
                    const itemKey = item.name + idx;
                    const prevItem = group.items[idx - 1];
                    const shouldRemoveTopBorder = item.level === 1 && prevItem?.level === 1;

                    return (
                      <div
                        key={itemKey}
                        className={`row lvl${item.level}`}
                        id={`frow-${group.id}-${item.name}-${idx}`}
                        style={shouldRemoveTopBorder ? { borderTop: 'none' } : undefined}
                        ref={registerHeightTarget}
                      >
                        {financialYears.map((year, yearIdx) => {
                          const foundItem = item.entries?.find((entry) => entry.year === year);
                          const value = foundItem?.value;

                          // Special handling for number of employees - display as-is without currency formatting.
                          // Matches the mapper: recognise via metric_group, falling back to the raw
                          // employee xbrl_codes on environments where metric_group isn't present yet.
                          const isEmployeeCount =
                            foundItem?.metric_group === METRIC_GROUP.EMPLOYEES ||
                            PROFILER_FINANCIAL_STATEMENTS.numberOfEmployees.xbrl_codes.includes(
                              foundItem?.xbrl_code
                            );
                          const displayValue = isEmployeeCount
                            ? value
                            : formatCurrency(value, PUNCTUATION.HYPHEN);

                          // TD-2277: mark self-disclosed values (source === 'basic')
                          // in blue with a hover, mirroring the overview. Only when a
                          // value is present for that year.
                          const isBasic = foundItem?.source === 'basic' && value != null;
                          const cellId = `fval-${group.id}-${idx}-${yearIdx}`;

                          return (
                            <div
                              className="row-cell"
                              key={year + '' + yearIdx}
                              style={{ width: columnWidth }}
                            >
                              <div
                                className="label"
                                style={isBasic ? { color: '#348cf9' } : undefined}
                              >
                                {isBasic ? (
                                  // Anchor the tooltip to a tight span around the value
                                  // (the cell is 157px wide and right-aligned, so anchoring
                                  // the full-width label would point the arrow at the cell
                                  // centre instead of the number). TD-2277.
                                  <span id={cellId}>{displayValue}</span>
                                ) : (
                                  displayValue
                                )}
                              </div>
                              {isBasic && (
                                <Tooltip
                                  anchorSelect={`#${cellId}`}
                                  multiline={true}
                                  variant="dark"
                                  place="bottom-center"
                                  opacity={1}
                                  content={<>Based on self-disclosure or estimate</>}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// PropTypes for complex data shapes
const FinancialEntryShape = PropTypes.shape({
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  xbrl_code: PropTypes.string,
  metric_group: PropTypes.string,
  source: PropTypes.string,
  legal_framework: PropTypes.string
});

const FinancialItemShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  level: PropTypes.number.isRequired,
  metric_group: PropTypes.string,
  entries: PropTypes.arrayOf(FinancialEntryShape)
});

const FinancialGroupShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  levels: PropTypes.arrayOf(PropTypes.number),
  legal_framework: PropTypes.object,
  reliability: PropTypes.object,
  items: PropTypes.arrayOf(FinancialItemShape)
});

const TabShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  isDisabled: PropTypes.bool,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func
});

const FinancialsDataShape = PropTypes.shape({
  detailsYears: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  groups: PropTypes.arrayOf(FinancialGroupShape)
});

FinancialsTab.propTypes = {
  // Controlled mode props
  tabs: PropTypes.arrayOf(TabShape),
  groups: PropTypes.arrayOf(FinancialGroupShape),
  financialYears: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  collapsedGroups: PropTypes.objectOf(PropTypes.bool),
  toggledFilters: PropTypes.objectOf(PropTypes.bool),
  selectedLevels: PropTypes.objectOf(PropTypes.number),
  onToggleFilter: PropTypes.func,
  onSetLevel: PropTypes.func,
  onToggleGroup: PropTypes.func,

  // Self-contained mode props
  financials: PropTypes.shape({
    details: PropTypes.shape({
      consolidated: FinancialsDataShape,
      individual: FinancialsDataShape
    }),
    general: PropTypes.object
  }),
  isConsolidatedDataEmpty: PropTypes.bool,
  isIndividualDataEmpty: PropTypes.bool,
  initialScope: PropTypes.oneOf(['consolidated', 'individual']),

  // Common props
  layout: PropTypes.oneOf(['desktop', 'mobile', 'tablet']),
  formatUnit: PropTypes.oneOf(['euroMillions', 'euroThousands']),
  formatCurrency: PropTypes.func
};

export default FinancialsTab;
